import { TranslationKey } from './../../../types/translation/TranslationKey';
import React, { useContext, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import StockLocationSettingsScreen from './StockLocation/StockLocationSettingsScreen';
import UserSettingsScreen from './User/UserSettingsScreen';
import DisplaySettingsScreen from './Company/DisplaySettingsScreen';
import { CompanyRoleAssignmentContext } from '../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../context/UserContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { SettingsScreenSidebarKey } from '../../../util/identifiers/SettingsScreenSidebarKey';
import { StockLocationRole } from '../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { StockLocationRoleAssignmentContext } from '../../../context/StockLocationRoleAssignmentContext';
import TriggersSettingsPane from './Triggers/TriggerSettingsScreen';
import { t, translate } from '../../../types/translation/Translator';
import { Sidebar, SidebarItem } from '../../components/Sidebar';
import Icon, { IconType } from '../../icons/Icon';
import StockLocationAdvancedSettingsPane from './StockLocation/Management/Settings/StockLocationAdvancedSettingsScreen';
import UserUpdatePane from './User/Update/UserUpdateScreen';
import UserAdvancedSettingsPane from './User/Settings/UserAdvancedSettingsScreen';
import Provider from '../../../providers/common/Provider';
import { Routing } from '../../../util/routes.util';
import SecuritySettingsScreen from './Security/SecuritySettingsScreen';
import ScanningSettingsScreen from './Scanning/ScanningSettingsScreen';
import StockLocationUpdateScreen from './StockLocation/Management/Update/StockLocationUpdateScreen';
import FeatureToggleSettingsScreen from './FeatureToggles/FeatureToggleSettingsScreen';
import CustomFieldSettingsScreen from './CustomField/CustomFieldSettingsScreen';
import IntegrationSettingsScreen from './Integration/IntegrationSettingsScreen';
import TagSettingsScreen from './Tag/TagSettingsScreen';
import StockLocationCreateScreen from './StockLocation/Management/Create/StockLocationCreateScreen';
import BarcodeSettingsScreen from './Barcodes/BarcodeSettingsScreen';
import BarcodeExportConfigurationUpdateScreen from './Barcodes/BarcodeExportConfigurationUpdateScreen';
import BarcodeExportConfigurationCreateScreen from './Barcodes/BarcodeExportConfigurationCreateScreen';
import ProductTransactionsScanRequiredWhitelistedUsersScreen from './FeatureToggles/Views/ProductTransactionsScanRequiredManageWhitelistedUsers';

export default function SettingsScreen() {
  return (
    <SettingsScreenProvider>
      <Element />
    </SettingsScreenProvider>
  );
}

function Element() {
  const location = useLocation();
  const navigate = useNavigate();

  const { currentUser } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { stockLocationRoles } = useContext(StockLocationRoleAssignmentContext);

  const items = useMemo(() => {
    const isStockLocationManager = !![...stockLocationRoles.values()]
      .flat()
      .find(slr => slr.userId === currentUser?.id && slr.role === StockLocationRole.STOCK_LOCATION_MANAGER);

    return [
      {
        text: t(TranslationKey.displaySettings).singular.upper,
        path: Routing.settings.display.details(),
        identifier: 'company',
        key: SettingsScreenSidebarKey.display,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.stockLocation).plural.upper,
        path: Routing.settings.stockLocations.overview(),
        identifier: 'stock_locations',
        key: SettingsScreenSidebarKey.stockLocations,
        shown: isAdmin || isStockLocationManager,
      },
      {
        text: t(TranslationKey.user).plural.upper,
        path: Routing.settings.users.overview(),
        identifier: 'users',
        key: SettingsScreenSidebarKey.users,
        shown: isAdmin || isStockLocationManager,
      },
      {
        text: t(TranslationKey.featureToggles).singular.upper,
        path: Routing.settings.featureToggles.overview(),
        identifier: 'feature_toggles',
        key: SettingsScreenSidebarKey.featureToggles,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.customFields).singular.upper,
        path: Routing.settings.customFields(),
        identifier: 'custom_fields',
        key: SettingsScreenSidebarKey.customFields,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.tag).plural.upper,
        path: Routing.settings.tags(),
        identifier: 'tags',
        key: SettingsScreenSidebarKey.featureToggles,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.triggers).singular.upper,
        path: Routing.settings.triggers(),
        identifier: 'triggers',
        key: SettingsScreenSidebarKey.triggers,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.scanning).singular.upper,
        path: Routing.settings.scanning.barcodeConfigurations(),
        identifier: 'scanning',
        key: SettingsScreenSidebarKey.scanning,
        shown: isAdmin,
      },
      {
        text: translate({ key: TranslationKey.barcode, count: 'plural' }),
        path: Routing.settings.barcodes.overview(),
        identifier: 'barcodes',
        key: SettingsScreenSidebarKey.barcodes,
        shown: isAdmin,
      },

      {
        text: t(TranslationKey.integrations).singular.upper,
        path: Routing.settings.integrations(),
        identifier: 'integrations',
        key: SettingsScreenSidebarKey.integrations,
        shown: isAdmin,
      },
      {
        text: t(TranslationKey.securityAndAccess).singular.upper,
        path: Routing.settings.security(),
        identifier: 'security',
        key: SettingsScreenSidebarKey.security,
        shown: isAdmin,
      },
    ].filter(item => item.shown);
  }, []);

  return (
    <div className='flex flex-grow overflow-hidden'>
      <Sidebar
        first={
          <div className='mx-4'>
            <div className='flex h-[52px] items-center py-2'>
              <button
                className='flex w-fit gap-2 rounded-2 px-3 py-2 hover:bg-gray-100'
                onClick={() => navigate(Routing.dashboard())}
              >
                <Icon type={IconType.arrowNarrowLeft} stroke={'stroke-black'} size={'h-5 w-5'} />
                <p className='text-sm font-medium text-black'>{t(TranslationKey.dashboard).singular.label}</p>
              </button>
            </div>
            <div className='mx-2 flex h-[76px] flex-col justify-center'>
              <p className='text-xl font-medium text-black'>Company Settings</p>
              <p className='text-sm text-gray-700'>{currentCompany.name}</p>
            </div>
          </div>
        }
        items={items.map(item => (
          <SidebarItem
            key={item.identifier}
            onClick={() => navigate(item.path)}
            title={item.text}
            identifier={item.identifier}
            selected={location.pathname.split('/settings/')[1]?.startsWith(item.identifier)}
          >
            <p>{item.text}</p>
          </SidebarItem>
        ))}
      />
      <Routes>
        <Route path='/users/update/:id/*' element={<UserUpdatePane />} />
        <Route path='/users/advanced/*' element={<UserAdvancedSettingsPane />} />

        <Route path='/stock_locations/update/:id/*' element={<StockLocationUpdateScreen />} />
        <Route path='/stock_locations/create/*' element={<StockLocationCreateScreen />} />
        <Route path='/stock_locations/advanced/*' element={<StockLocationAdvancedSettingsPane />} />

        <Route path='/barcodes/create/*' element={<BarcodeExportConfigurationCreateScreen />} />
        <Route path='/barcodes/update/:id/*' element={<BarcodeExportConfigurationUpdateScreen />} />

        <Route
          path='/feature_toggles/product_transactions_scan_required_whitelisted_users'
          element={<ProductTransactionsScanRequiredWhitelistedUsersScreen />}
        />

        <Route
          path='*'
          element={
            <Routes>
              <Route path='/*' element={<Navigate to={Routing.settings.display.details()} />} />
              <Route path='/company/*' element={<DisplaySettingsScreen />} />
              <Route path='/scanning/*' element={<ScanningSettingsScreen />} />
              <Route path='/feature_toggles/*' element={<FeatureToggleSettingsScreen />} />
              <Route path='/triggers/*' element={<TriggersSettingsPane />} />
              <Route path='/users/*' element={<UserSettingsScreen />} />
              <Route path='/stock_locations/*' element={<StockLocationSettingsScreen />} />
              <Route path='/custom_fields/*' element={<CustomFieldSettingsScreen />} />
              <Route path='/integrations/*' element={<IntegrationSettingsScreen />} />
              <Route path='/tags/*' element={<TagSettingsScreen />} />
              <Route path='/barcodes/*' element={<BarcodeSettingsScreen />} />
              <Route path='/security/*' element={<SecuritySettingsScreen />} />
            </Routes>
          }
        />
      </Routes>
    </div>
  );
}

function SettingsScreenProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider
      authenticationSettings
      barcodeExportConfiguration
      bin
      binStatus
      containerType
      csvUploadConfiguration
      customField
      lot
      productMasterData
      scanRule
      stockLocation
      stockLocationRoleAssignment
      tag
      file
      tagRelation
      trigger
      user
      integration
      productTransaction
      container
      order
      orderFlows
    >
      {children}
    </Provider>
  );
}
